import { useEffect, useState } from 'react';
import { Button, Spinner } from '@fluentui/react-components';
import type { FormField, FormResponses, ProjectContext } from '@qcbot/shared';
import { fetchQcTemplateForm } from '../api';
import { applyFormDefaults, DynamicFormField, isFormValid } from './DynamicFormField';

interface Props {
  onSubmit: (context: ProjectContext, formResponses: FormResponses) => void;
  onBack: () => void;
  submitting: boolean;
  /** The QC template picked on the previous step — its own form fields (if any) render here.
   * Undefined/no fields renders nothing extra. */
  qcTemplateId?: string;
  /** Restores previously-entered answers if the user went Back to this step and returned. */
  initialFormResponses?: FormResponses;
}

// clientName/projectName/deckVersion are still required by the shared ProjectContext type (and
// tolerated as optional-in-practice downstream, e.g. deterministicChecks.ts's `|| 'n/a'`), but are
// no longer collected here — that project-specific detail now lives entirely in each QC template's
// own configurable form fields (see admin QcFormBuilderPage), not this hardcoded shape.
// reviewType/priorRunId are likewise fixed at 'new'/undefined — neither is actually read by any
// downstream check (deterministicChecks.ts, dedup.ts), so the old new-vs-continuation picker this
// used to drive was pure unused UI; removed rather than kept around for a distinction nothing acts on.
export const emptyContext: ProjectContext = {
  clientName: '',
  projectName: '',
  deckVersion: '',
  reviewType: 'new',
  priorRunId: undefined,
};

/**
 * Second step of a review run: the selected QC template's own configurable form fields. Any
 * ad-hoc rules for this run are collected on the previous step (ProjectTemplateForm).
 */
export function ProjectContextForm({ onSubmit, onBack, submitting, qcTemplateId, initialFormResponses }: Props) {
  const [context] = useState<ProjectContext>(emptyContext);
  const [templateFields, setTemplateFields] = useState<FormField[]>([]);
  const [templateResponses, setTemplateResponses] = useState<FormResponses>({});
  const [loadingTemplateFields, setLoadingTemplateFields] = useState(Boolean(qcTemplateId));

  useEffect(() => {
    if (!qcTemplateId) {
      setTemplateFields([]);
      setTemplateResponses({});
      setLoadingTemplateFields(false);
      return;
    }
    setLoadingTemplateFields(true);
    fetchQcTemplateForm(qcTemplateId)
      .then(({ fields: loaded }) => {
        setTemplateFields(loaded);
        setTemplateResponses(applyFormDefaults(loaded, initialFormResponses ?? {}));
      })
      .finally(() => setLoadingTemplateFields(false));
    // Only re-fetch if the template itself changes (e.g. user went Back and picked another one)
    // — not on every initialFormResponses identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qcTemplateId]);

  function updateTemplateResponse(fieldKey: string, value: unknown) {
    setTemplateResponses((prev) => ({ ...prev, [fieldKey]: value }));
  }

  const canSubmit = Boolean(!loadingTemplateFields && isFormValid(templateFields, templateResponses));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {loadingTemplateFields ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#666' }}>
          <Spinner size="tiny" /> Loading QC template fields…
        </div>
      ) : (
        templateFields.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {templateFields.map((field) => (
              <DynamicFormField key={field.id} field={field} value={templateResponses[field.fieldKey]} onChange={updateTemplateResponse} />
            ))}
          </div>
        )
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button appearance="secondary" disabled={submitting} onClick={onBack}>
          Back
        </Button>
        <Button appearance="primary" disabled={!canSubmit || submitting} onClick={() => onSubmit(context, templateResponses)}>
          {submitting ? 'Starting…' : 'Run QC review'}
        </Button>
      </div>
    </div>
  );
}
